import { CacheService } from "./CacheService";

export interface RequestOptions {
  params?: Record<string, string>;
  body?: unknown;
  headers?: Record<string, string>;
}

const DEFAULT_BASE_URL =
  "https://3nmoaxzhf9.execute-api.us-east-1.amazonaws.com/prod/";

function getBaseUrl(): string {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'development') {
    return '';
  }
  return DEFAULT_BASE_URL;
}

function getApiKey(): string | undefined {
  return process.env.REACT_APP_API_KEY || undefined;
}

export class BaseService {
  protected baseRoute: string;

  constructor(baseRoute: string) {
    this.baseRoute = baseRoute;
  }

  protected async get<T>(route?: string, options?: RequestOptions): Promise<T> {
    return this.request<T>("GET", route, options);
  }

  protected async post<T>(
    route?: string,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request<T>("POST", route, options);
  }

  protected async put<T>(
    route?: string,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request<T>("PUT", route, options);
  }

  protected async delete<T>(
    route?: string,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request<T>("DELETE", route, options);
  }

  async patch<T>(route?: string, options?: RequestOptions): Promise<T> {
    return this.request<T>("PATCH", route, options);
  }

  private inflightRequests = new Map<string, Promise<unknown>>();

  private async request<T>(
    method: string,
    route?: string,
    options?: RequestOptions,
  ): Promise<T> {
    const baseUrl = getBaseUrl();
    const path = this.buildPath(route, options);
    const url = `${baseUrl.replace(/\/+$/, "")}/${path.replace(/^\//, "")}`;

    const cacheKey = `${method}:${url}`;

    if (method === "GET") {
      const cached = CacheService.getInstance().get<T>(cacheKey);
      if (cached !== undefined) {
        return cached;
      }

      const inflight = this.inflightRequests.get(cacheKey);
      if (inflight) {
        console.log(`[Cache] DEDUP ${cacheKey} (reusing in-flight request)`);
        return inflight as Promise<T>;
      }
    } else {
      CacheService.getInstance().invalidate(this.baseRoute);
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...options?.headers,
    };

    const apiKey = getApiKey();
    if (apiKey) {
      headers["x-api-key"] = apiKey;
    }

    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    const body = options?.body && method !== "GET" ? options.body : undefined;
    if (body) {
      fetchOptions.body = JSON.stringify(body);
    }

    console.log(`[API] ${method} ${url}`, body ? { body } : "");

    const promise = fetch(url, fetchOptions)
      .then(async (response) => {
        if (response.status === 404) {
          console.log(`[API] 404 ${method} ${url} → null`);
          return null as T;
        }

        if (!response.ok) {
          const errMsg = `Erro na requisição: ${response.status} ${response.statusText}`;
          console.error(`[API] ${method} ${url} → ${errMsg}`);
          throw new Error(errMsg);
        }

        if (response.status === 204) {
          console.log(`[API] 204 ${method} ${url} → (no content)`);
          return undefined as T;
        }

        const data = await response.json();
        console.log(`[API] ${method} ${url} →`, data);

        if (method === "GET") {
          const ttl = this.getCacheTTL(url);
          CacheService.getInstance().set(cacheKey, data, ttl);
        }

        return data as T;
      })
      .finally(() => {
        this.inflightRequests.delete(cacheKey);
      });

    if (method === "GET") {
      this.inflightRequests.set(cacheKey, promise);
    }

    return promise;
  }

  private getCacheTTL(url: string): number {
    if (url.includes("/neighborhoods") || url.includes("/cities")) return 600;
    if (url.includes("/destaque")) return 300;
    if (/\/imoveis\/[^\/?]+\s*$/.test(url)) return 300;
    if (url.includes("/imoveis") && url.includes("?")) return 30;
    return 60;
  }

  private buildPath(route?: string, options?: RequestOptions): string {
    const cleanRoute = route?.replace(/^\//, "") || "";
    const path = cleanRoute
      ? `${this.baseRoute}/${cleanRoute}`
      : this.baseRoute;

    if (options?.params) {
      const qs = new URLSearchParams(options.params).toString();
      return `${path}?${qs}`;
    }

    return path;
  }
}
